/**
 * fiko-occt-kernel.ts — GERÇEK B-Rep kernel (OpenCascade.js) köprüsü.
 *
 * Hibrit planın 2. ayağı: mesh-CSG (FikoKatiMotoru) yaklaşık fillet/shell verir;
 * OCCT KUSURSUZ verir (BRepFilletAPI/BRepOffsetAPI). public/opencascade/ altındaki
 * tam OCCT WASM modülleri lazy yüklenir (~40MB, bir kez). Mevcut sistemi BOZMAZ —
 * opsiyonel kernel; init edilmeden hiçbir şey yüklenmez.
 *
 * Toolkit yükleme SIRASI kritik (bağımlılık): TKGeomAlgo TKBRep'ten SONRA olmalı.
 */
import * as THREE from 'three';
import { FikoTopoKimlik } from './fiko-toporef';

const OCCT_KOK = '/opencascade/';
// Modeling için gereken toolkit'ler — bağımlılık sırasıyla (math→geom→brep→geomalgo→topo→prim→bool→fillet→offset→mesh)
const LIBS = ['TKMath', 'TKG2d', 'TKG3d', 'TKGeomBase', 'TKBRep', 'TKGeomAlgo', 'TKTopAlgo', 'TKShHealing', 'TKPrim', 'TKBO', 'TKBool', 'TKFillet', 'TKOffset', 'TKMesh'];
// STEP okuma/yazma toolkit'leri (ayrı lazy yüklenir — sadece STEP import/export'ta)
const STEP_LIBS = ['TKXSBase', 'TKSTEPBase', 'TKSTEPAttr', 'TKSTEP209', 'TKSTEP'];

export class FikoOCCTKernel {
  private oc: any = null;
  private initPromise: Promise<any> | null = null;
  private stepHazir = false;
  private stepPromise: Promise<void> | null = null;

  hazirMi(): boolean { return !!this.oc; }
  get ocRaw(): any { return this.oc; }

  /** OCCT'yi lazy yükler (dynamic import + toolkit kütüphaneleri). İlk çağrı ~3-8sn. */
  async init(): Promise<any> {
    if (this.oc) return this.oc;
    if (this.initPromise) return this.initPromise;
    this.initPromise = (async () => {
      const mod: any = await import(/* @vite-ignore */ (OCCT_KOK + 'opencascade.js'));
      const Module = mod.default || mod;
      const oc = await Module({ locateFile: (p: string) => p.endsWith('.wasm') ? OCCT_KOK + p : p });
      for (const lib of LIBS) {
        await oc.loadDynamicLibrary(OCCT_KOK + 'module.' + lib + '.wasm', { loadAsync: true, global: true, nodelete: true, allowUndefined: false });
      }
      this.oc = oc;
      if (typeof window !== 'undefined' && typeof ngDevMode !== 'undefined' && ngDevMode) (window as any).__oc = oc;   // DEV-only: OCCT API sondajı (prod'da tree-shake)
      return oc;
    })();
    return this.initPromise;
  }

  /** STEP okuma/yazma toolkit'lerini lazy yükler (sadece STEP import/export'ta, ~ek 5 toolkit). */
  async stepYukle(): Promise<void> {
    if (this.stepHazir) return;
    if (this.stepPromise) return this.stepPromise;
    this.stepPromise = (async () => {
      const oc = await this.init();
      for (const lib of STEP_LIBS) {
        await oc.loadDynamicLibrary(OCCT_KOK + 'module.' + lib + '.wasm', { loadAsync: true, global: true, nodelete: true, allowUndefined: false });
      }
      this.stepHazir = true;
    })();
    try { await this.stepPromise; } catch (e) { this.stepPromise = null; throw e; }   // başarısızsa cache'leme (tekrar denenebilsin)
  }

  /** STEP dosya byte'larını GERÇEK B-Rep TopoDS_Shape olarak okur (kusursuz — pocket/fillet uygulanabilir). */
  async stepOku(bytes: Uint8Array): Promise<any | null> {
    await this.stepYukle();
    const oc = this.oc;
    const yol = '/i.stp';   // OCCT WASM ReadFile uzun dosya adında RetFail veriyor — KISA ad şart
    try {
      oc.FS.writeFile(yol, bytes);
      const reader = new oc.STEPControl_Reader_1();
      const durum = reader.ReadFile(yol);
      const dv = (durum && durum.value !== undefined) ? durum.value : durum;   // embind enum: değerle karşılaştır (kimlik güvenilmez)
      const rd = oc.IFSelect_ReturnStatus.IFSelect_RetDone;
      if (dv !== ((rd && rd.value !== undefined) ? rd.value : rd)) { try { oc.FS.unlink(yol); } catch { } return null; }
      reader.TransferRoots(new oc.Message_ProgressRange_1());
      const shape = reader.OneShape();
      try { oc.FS.unlink(yol); } catch { }
      return shape;
    } catch (e) { console.warn('[OCCT] stepOku hatası', e); try { oc.FS.unlink(yol); } catch { } return null; }
  }

  /** TopoDS_Shape'i STEP byte'larına yazar (export). */
  async stepYaz(shape: any): Promise<Uint8Array | null> {
    await this.stepYukle();
    const oc = this.oc;
    const yol = '/o.stp';   // kısa ad (OCCT WASM uzun ad sorunu)
    try {
      const writer = new oc.STEPControl_Writer_1();
      writer.Transfer(shape, oc.STEPControl_StepModelType.STEPControl_AsIs, true, new oc.Message_ProgressRange_1());
      writer.Write(yol);
      const bytes = oc.FS.readFile(yol);
      try { oc.FS.unlink(yol); } catch { }
      return bytes;
    } catch (e) { console.warn('[OCCT] stepYaz hatası', e); return null; }
  }

  /** OCCT TopoDS_Shape → THREE.BufferGeometry (BRepMesh tessellation + yüz transform'ları).
   * CATIA "3D Accuracy": oran = oransal sag (eleman boyutuna göre, küçük=ince), aci = eğrilik (açısal sapma rad, küçük=pürüzsüz). */
  shapeToGeo(shape: any, sapma = 0.1, aci = 0.5, oran = 0.004): THREE.BufferGeometry | null {
    const oc = this.oc; if (!oc || !shape) return null;
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };   // OCCT embind objesi WASM heap'ten serbest bırak (leak önleme)
    // ADAPTIVE DEFLECTION: model boyutuna göre — küçükte ince kalite, büyükte gevşet (oran ile ölçeklenir = CATIA 3D Accuracy)
    let adapt = sapma;
    try { const bb = new oc.Bnd_Box_1(); oc.BRepBndLib.Add(shape, bb, false); const mn = bb.CornerMin(), mx = bb.CornerMax(); const r = Math.hypot(mx.X() - mn.X(), mx.Y() - mn.Y(), mx.Z() - mn.Z()) * 0.5; adapt = Math.max(sapma, Math.min(r * oran, 3)); sil(mn); sil(mx); sil(bb); } catch { }
    const im = new oc.BRepMesh_IncrementalMesh_2(shape, adapt, false, aci, false);
    // NON-INDEXED + her OCCT YÜZÜ ayrı grup (x=strt, y=vertexCount, z=faceId) → FikoSecililer
    // B-Rep yüz seçimi NATIVE (teğet fillet'ler düz yüze birleşmez; yuzGrupla geometrik gerekmez).
    const pos: number[] = [], grup: number[] = [];
    let faceId = 0;
    const fexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (fexp.More()) {
      const face = oc.TopoDS.Face_1(fexp.Current());
      const loc = new oc.TopLoc_Location_1();
      const triH = oc.BRep_Tool.Triangulation(face, loc);
      if (!triH.IsNull()) {
        const t = triH.get();
        const trsf = loc.Transformation();
        const nb = t.NbNodes();
        const nd: number[] = [];
        for (let i = 1; i <= nb; i++) { const node = t.Node(i); const p = node.Transformed(trsf); nd.push(p.X(), p.Y(), p.Z()); sil(p); sil(node); }
        let ters = false;
        try { const ori = face.Orientation_1(); ters = ori.value === (oc.TopAbs_Orientation.TopAbs_REVERSED?.value ?? 1); sil(ori); } catch { }
        const yuzStrt = pos.length / 3;
        const nt = t.NbTriangles();
        for (let i = 1; i <= nt; i++) {
          const tr = t.Triangle(i); let a = tr.Value(1), b = tr.Value(2), c = tr.Value(3); if (ters) { const x = b; b = c; c = x; } sil(tr);
          for (const vi of [a, b, c]) { const o = (vi - 1) * 3; pos.push(nd[o], nd[o + 1], nd[o + 2]); }   // FLAT (non-indexed)
        }
        const yuzAdet = pos.length / 3 - yuzStrt;
        for (let k = 0; k < yuzAdet; k++) grup.push(yuzStrt, yuzAdet, faceId);   // bu yüzün tüm vertex'lerine grup
        faceId++;
        sil(trsf);
      }
      sil(triH); sil(loc); sil(face);
      fexp.Next();
    }
    sil(fexp); sil(im);   // explorer + incremental-mesh geçici objelerini serbest bırak
    if (!pos.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('grup', new THREE.Float32BufferAttribute(grup, 3));   // B-Rep yüz grupları
    g.computeVertexNormals();
    (g as any)._occtFaceGruplu = true;   // rebuildOCCT yuzGrupla'yı atlasın
    return g;
  }

  /** B-REP KENARLAR: her OCCT edge'i parametrik örnekleyip polyline döndürür (paylaşılan kenar TEK kez).
   * → fillet sonrası teğet kenarlar dahil GERÇEK topoloji kenarları seçilebilir. */
  shapeToEdges(shape: any): { pts: { x: number; y: number; z: number }[] }[] {
    const oc = this.oc; if (!oc || !shape) return [];
    const out: { pts: { x: number; y: number; z: number }[] }[] = [];
    const seen = new Set<string>();
    const Q = 1e-3, rd = (v: number) => Math.round(v / Q);
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const eexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (eexp.More()) {
      try {
        const edge = oc.TopoDS.Edge_1(eexp.Current());
        const curve = new oc.BRepAdaptor_Curve_2(edge);
        const u0 = curve.FirstParameter(), u1 = curve.LastParameter();
        if (isFinite(u0) && isFinite(u1) && u1 > u0) {
          const N = 24, pts: { x: number; y: number; z: number }[] = [];
          for (let i = 0; i <= N; i++) { const u = u0 + (u1 - u0) * (i / N); const p = curve.Value(u); pts.push({ x: p.X(), y: p.Y(), z: p.Z() }); sil(p); }
          // paylaşılan kenar dedup: uç noktalar + ORTA NOKTA anahtarı.
          // (Sadece uç noktayla dedup, bir çemberin iki yarım-yayını -aynı uçlar- mükerrer sanıp ikincisini siliyordu → çember yarım çiziliyordu.
          //  Orta nokta eklenince iki yarım-yay AYRI sayılır (ikisi de çizilir); GERÇEK mükerrer kenar -uç+orta aynı- yine silinir.)
          const a = pts[0], b = pts[pts.length - 1], mid = pts[Math.floor(pts.length / 2)];
          const ka = `${rd(a.x)},${rd(a.y)},${rd(a.z)}`, kb = `${rd(b.x)},${rd(b.y)},${rd(b.z)}`;
          const km = `${rd(mid.x)},${rd(mid.y)},${rd(mid.z)}`;
          const key = (ka < kb ? ka + '|' + kb : kb + '|' + ka) + '|' + km;
          if (!seen.has(key)) {
            seen.add(key);
            if (pts.length >= 2) {
              const grup: any = { pts };
              // ANALİTİK zenginleştirme (ölçüm/snap GERÇEK merkez+yarıçap kullansın — FikoGeometricSnap curveType/center okur)
              try {
                if (curve.GetType?.() === oc.GeomAbs_CurveType?.GeomAbs_Circle) {
                  const ci = curve.Circle(); const lo = ci.Location(); const ax = ci.Axis().Direction();
                  grup.curveType = 'CIRCLE'; grup.center = { x: lo.X(), y: lo.Y(), z: lo.Z() }; grup.radius = ci.Radius(); grup.axis = { x: ax.X(), y: ax.Y(), z: ax.Z() };
                  sil(lo); sil(ax); sil(ci);
                } else if (curve.GetType?.() === oc.GeomAbs_CurveType?.GeomAbs_Line) {
                  grup.curveType = 'LINE';
                }
              } catch { }
              out.push(grup);
            }
          }
        }
        sil(curve); sil(edge);
      } catch { }
      eexp.Next();
    }
    sil(eexp);
    return out;
  }

  // ───────────────────── B-REP KÖPRÜSÜ (mesh faceId → gerçek TopoDS_Face geometrisi) ─────────────────────
  /** Mesh grup.z=faceId'ye karşılık gelen TopoDS_Face'i döndürür. shapeToGeo TopExp(FACE) sırasıyla faceId atadığı için
   * aynı sıralı gezinme N'inci yüzü verir. Çağıran .delete() etmeli. */
  yuzAl(shape: any, faceId: number): any {
    const oc = this.oc; if (!oc || !shape || faceId < 0) return null;
    const fexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    let i = 0, sonuc: any = null;
    while (fexp.More()) {
      if (i === faceId) { sonuc = oc.TopoDS.Face_1(fexp.Current()); break; }
      i++; fexp.Next();
    }
    try { fexp.delete(); } catch { }
    return sonuc;
  }

  // ───────────────────── PERSISTENT NAMING (TNaming): yüz kalıcı kimliği ─────────────────────
  // rebuildOCCT bir "running shape" üzerinde sırayla op uygular. Aktif kimlik varken boolean/fillet/
  // chamfer/transform op'ları OCCT history (Modified/IsDeleted) ile stableId'leri TAŞIR. Replay
  // deterministik olduğundan bir fiziksel yüzün stableId'si rebuild'ler arası sabit → radius eklense
  // de seçim doğru yüze bağlı kalır. Hata olursa sessizce yeniden-tabanlanır (centroid fallback devrede).
  private _kimlik: FikoTopoKimlik | null = null;
  private _kimlikShape: any = null;

  /** Bir shape'in yüzlerini faceId (TopExp) sırasında dizi olarak verir. */
  private yuzListesi(shape: any): any[] {
    const oc = this.oc; const out: any[] = []; if (!oc || !shape) return out;
    const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More()) { out.push(oc.TopoDS.Face_1(exp.Current())); exp.Next(); }
    try { exp.delete(); } catch { }
    return out;
  }

  /** ListOfShape → düz dizi. */
  private listeDizi(list: any): any[] {
    const oc = this.oc; const out: any[] = []; if (!list) return out;
    try { const it = new oc.TopTools_ListIteratorOfListOfShape_2(list); while (it.More()) { out.push(it.Value()); it.Next(); } } catch { }
    return out;
  }

  /** yuzler içinde hedef yüzle AYNI (IsSame) olanın index'i, yoksa -1. */
  private yuzIndexBul(yuzler: any[], hedef: any): number {
    for (let i = 0; i < yuzler.length; i++) { try { if (yuzler[i].IsSame(hedef)) return i; } catch { } }
    return -1;
  }

  /** Aktif kimliği shape'in tüm yüzlerine taze stableId atayarak başlatır (taban/yeniden-taban). */
  kimlikBaslat(shape: any): void {
    if (!this.oc || !shape) { this._kimlik = null; this._kimlikShape = null; return; }
    const k = new FikoTopoKimlik();
    const n = this.yuzListesi(shape).length;
    for (let i = 0; i < n; i++) k.yuzId[i] = k.tazeId();
    this._kimlik = k; this._kimlikShape = shape;
  }
  kimlikDurdur(): void { this._kimlik = null; this._kimlikShape = null; }
  /** Verilen faceId'nin kalıcı stableId'si (aktif kimlikte). Yoksa -1. */
  kimlikYuzId(faceId: number): number { const k = this._kimlik; return (k && faceId >= 0 && faceId < k.yuzId.length) ? (k.yuzId[faceId] ?? -1) : -1; }
  /** stableId → şu anki faceId (aktif kimlikte). Yoksa -1. */
  kimlikFaceId(stableId: number): number { const k = this._kimlik; if (!k || stableId < 0) return -1; const i = k.yuzId.indexOf(stableId); return i; }

  /**
   * Op sonrası kimlik taşı. builder = OCCT MakeShape (Modified/IsDeleted sağlar). a = takip edilen
   * "running shape" (eskiShape); b = ikinci girdi (boolean tool, opsiyonel). yeni = sonuç shape.
   * a !== _kimlikShape ise (tool montaj op'u) DOKUNMAZ. Hata → yeniden-tabanla (fallback güvenli).
   */
  private kimlikGecis(builder: any, a: any, yeni: any): any {
    const k = this._kimlik;
    if (!k || a !== this._kimlikShape || !builder || !yeni) return yeni;   // takip edilmeyen op → karışma
    try {
      const yeniYuzler = this.yuzListesi(yeni);
      const yeniId: number[] = new Array(yeniYuzler.length).fill(0);
      const tasi = (girdiYuzler: any[], idDizi: number[] | null) => {
        for (let i = 0; i < girdiYuzler.length; i++) {
          const fa = girdiYuzler[i];
          const sid = idDizi ? (idDizi[i] ?? 0) : 0;   // b için kimlik yok → 0 (taze alır)
          let silindi = false; try { silindi = !!builder.IsDeleted(fa); } catch { }
          if (silindi) continue;
          let hedefler: any[] = []; try { hedefler = this.listeDizi(builder.Modified(fa)); } catch { }
          if (!hedefler.length) hedefler = [fa];   // değişmedi → birebir geçer
          for (const t of hedefler) { const ni = this.yuzIndexBul(yeniYuzler, t); if (ni >= 0 && !yeniId[ni] && sid) yeniId[ni] = sid; }
        }
      };
      tasi(this.yuzListesi(a), k.yuzId);          // running shape yüzleri → stableId taşı
      for (let j = 0; j < yeniId.length; j++) if (!yeniId[j]) yeniId[j] = k.tazeId();   // b'nin yüzleri + yeni/Generated yüzler → taze
      k.yuzId = yeniId; this._kimlikShape = yeni;
      return yeni;
    } catch (e) { this.kimlikBaslat(yeni); return yeni; }   // herhangi hata → yeniden-tabanla (centroid fallback devrede)
  }

  /** Transform sonucu (öteleme/döndür/ayna/ölçek). _kimlikShape'i DEĞİŞTİRMEZ: ara transform'lar
   *  (mirror/pattern'da birlestir(shape, mirror(shape))) tracking'i ele geçirmesin. Gerçek transform
   *  feature'ında sıra korunduğu için bir sonraki kimlikSenkron yuzId'yi yeni shape'e taşır. */
  private kimlikTransformTasi(_a: any, yeni: any): any { return yeni; }

  /** Feature başında kimliği gövdeyle hizala: aynıysa geç; yüz sayısı aynıysa (sıra-koruyan transform)
   *  yuzId'yi taşı; farklıysa (topoloji değişti / izlenmeyen op) yeniden-tabanla (fallback devrede). */
  kimlikSenkron(shape: any): void {
    const k = this._kimlik; if (!k || !shape) return;
    if (this._kimlikShape === shape) return;
    const n = this.yuzListesi(shape).length;
    if (n === k.yuzId.length) this._kimlikShape = shape;   // sıra korundu → yuzId geçerli
    else this.kimlikBaslat(shape);                          // topoloji ayrıştı → taze kimlik
  }

  /** B-REP YÜZ KONTURU: bir yüzün dış sınırını ANALİTİK segmentlerle döndürür (çizgi/yay sınıflı; eğri→polyline).
   * CATIA modeli: mesh değil gerçek topoloji kenarı. CAM/ölçüm/kesit bunu kullanır. arc: cx/cy/cz/r/cw (XY varsayımı). */
  yuzKonturBrep(shape: any, faceId: number): { segmentler: { tip: 'line' | 'arc' | 'egri'; noktalar: { x: number; y: number; z: number }[]; arc?: { cx: number; cy: number; cz: number; r: number; cw: boolean } }[]; kapali: boolean } | null {
    const oc = this.oc; if (!oc || !shape) return null;
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const face = this.yuzAl(shape, faceId); if (!face) return null;
    const segmentler: any[] = [];
    let wire: any = null, wexp: any = null;
    try {
      wire = oc.BRepTools.OuterWire(face);   // dış sınır teli (delik/iç döngü hariç)
      wexp = new oc.BRepTools_WireExplorer_2(wire);   // BAĞLI SIRADA kenarlar
      while (wexp.More()) {
        let edge: any = null, curve: any = null;
        try {
          edge = oc.TopoDS.Edge_1(wexp.Current());
          const ters = edge.Orientation?.() === oc.TopAbs_Orientation?.TopAbs_REVERSED;
          curve = new oc.BRepAdaptor_Curve_2(edge);
          let u0 = curve.FirstParameter(), u1 = curve.LastParameter();
          const tip = curve.GetType?.();   // GeomAbs_CurveType
          const P = (u: number) => { const p = curve.Value(u); const o = { x: p.X(), y: p.Y(), z: p.Z() }; sil(p); return o; };
          if (tip === oc.GeomAbs_CurveType?.GeomAbs_Line) {
            const a = P(u0), b = P(u1); segmentler.push({ tip: 'line', noktalar: ters ? [b, a] : [a, b] });
          } else if (tip === oc.GeomAbs_CurveType?.GeomAbs_Circle) {
            const circ = curve.Circle();   // gp_Circ
            const loc = circ.Location(); const cx = loc.X(), cy = loc.Y(), cz = loc.Z(); const r = circ.Radius();
            const ax = circ.Axis().Direction(); const cw = (ax.Z() < 0) !== ters;   // XY düzleminde dönüş yönü
            const a = P(u0), b = P(u1);
            segmentler.push({ tip: 'arc', noktalar: ters ? [b, a] : [a, b], arc: { cx, cy, cz, r, cw } });
            sil(loc); sil(ax); sil(circ);
          } else {
            const N = 16, pts: any[] = []; for (let k = 0; k <= N; k++) pts.push(P(u0 + (u1 - u0) * (k / N)));
            segmentler.push({ tip: 'egri', noktalar: ters ? pts.reverse() : pts });
          }
        } catch { }
        sil(curve); sil(edge);
        wexp.Next();
      }
    } catch { } finally { sil(wexp); sil(wire); sil(face); }
    return segmentler.length ? { segmentler, kapali: true } : null;
  }

  /** B-REP YÜZ KONTUR OFFSET: yüzün dış konturunu delta kadar EXACT offset'ler (yay-korumalı, Clipper'sız).
   * delta<0 içe (cep), >0 dışa. sharp=true sivri köşe (jtMiter/GeomAbs_Intersection), false yuvarlak.
   * Dönüş yuzKonturBrep ile aynı: sıralı LINE/ARC segment (CAM exact offset toolpath için). Tarayıcıda doğrulandı. */
  yuzKonturOffset(shape: any, faceId: number, delta: number, sharp = true): { segmentler: { tip: 'line' | 'arc' | 'egri'; noktalar: { x: number; y: number; z: number }[]; arc?: { cx: number; cy: number; cz: number; r: number; cw: boolean } }[]; kapali: boolean } | null {
    const oc = this.oc; if (!oc || !shape) return null;
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const face = this.yuzAl(shape, faceId); if (!face) return null;
    const segmentler: any[] = [];
    let wire: any = null, mo: any = null, wexp: any = null;
    try {
      wire = oc.BRepTools.OuterWire(face);
      const join = sharp ? oc.GeomAbs_JoinType.GeomAbs_Intersection : oc.GeomAbs_JoinType.GeomAbs_Arc;
      mo = new oc.BRepOffsetAPI_MakeOffset_3(wire, join, false);
      mo.Perform(delta, 0);
      const off = mo.Shape(); if (!off) return null;
      const offWire = oc.TopoDS.Wire_1(off);
      wexp = new oc.BRepTools_WireExplorer_2(offWire);
      while (wexp.More()) {
        let edge: any = null, curve: any = null;
        try {
          edge = oc.TopoDS.Edge_1(wexp.Current());
          const ters = edge.Orientation?.() === oc.TopAbs_Orientation?.TopAbs_REVERSED;
          curve = new oc.BRepAdaptor_Curve_2(edge);
          const u0 = curve.FirstParameter(), u1 = curve.LastParameter();
          const tip = curve.GetType?.();
          const P = (u: number) => { const p = curve.Value(u); const o = { x: p.X(), y: p.Y(), z: p.Z() }; sil(p); return o; };
          if (tip === oc.GeomAbs_CurveType?.GeomAbs_Line) {
            const a = P(u0), b = P(u1); segmentler.push({ tip: 'line', noktalar: ters ? [b, a] : [a, b] });
          } else if (tip === oc.GeomAbs_CurveType?.GeomAbs_Circle) {
            const circ = curve.Circle(); const loc = circ.Location(); const cx = loc.X(), cy = loc.Y(), cz = loc.Z(); const r = circ.Radius();
            const ax = circ.Axis().Direction(); const cw = (ax.Z() < 0) !== ters; const a = P(u0), b = P(u1);
            segmentler.push({ tip: 'arc', noktalar: ters ? [b, a] : [a, b], arc: { cx, cy, cz, r, cw } }); sil(loc); sil(ax); sil(circ);
          } else {
            const N = 16, pts: any[] = []; for (let k = 0; k <= N; k++) pts.push(P(u0 + (u1 - u0) * (k / N))); segmentler.push({ tip: 'egri', noktalar: ters ? pts.reverse() : pts });
          }
        } catch { }
        sil(curve); sil(edge); wexp.Next();
      }
    } catch (e) { console.warn('[OCCT] yuzKonturOffset hata', e); } finally { sil(wexp); sil(mo); sil(wire); sil(face); }
    return segmentler.length ? { segmentler, kapali: true } : null;
  }

  /** GENEL 2D POLİGON EXACT OFFSET (CAM toolpath): XY noktalarından kapalı wire kurup BRepOffsetAPI ile offsetler.
   * dIce>0 = İÇE (küçültme), dIce<0 = DIŞA (büyütme) — FikoCamToolpath offsetPoly konvansiyonu (yön alan karşılaştırmasıyla garanti edilir).
   * Topoloji-seviyesi offset: self-intersection/keskin köşe patlaması OLMAZ. Kernel hazır değil/başarısız → null (çağıran JS fallback). */
  poligonOffset2D(poly: { x: number; y: number }[], dIce: number, sharp = true): { x: number; y: number }[] | null {
    const oc = this.oc; if (!oc || !poly || poly.length < 3 || Math.abs(dIce) < 1e-6) return null;
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const alan2 = (p: { x: number; y: number }[]) => { let a = 0; for (let i = 0, n = p.length; i < n; i++) { const j = (i + 1) % n; a += p[i].x * p[j].y - p[j].x * p[i].y; } return Math.abs(a / 2); };
    const dene = (delta: number): { x: number; y: number }[] | null => {
      let mp: any = null, mo: any = null, wexp: any = null;
      try {
        mp = new oc.BRepBuilderAPI_MakePolygon_1();
        for (const p of poly) mp.Add_1(new oc.gp_Pnt_3(p.x, p.y, 0));
        mp.Close();
        const wire = mp.Wire();
        const join = sharp ? oc.GeomAbs_JoinType.GeomAbs_Intersection : oc.GeomAbs_JoinType.GeomAbs_Arc;
        mo = new oc.BRepOffsetAPI_MakeOffset_3(wire, join, false);
        mo.Perform(delta, 0);
        const off = mo.Shape(); if (!off) return null;
        const offWire = oc.TopoDS.Wire_1(off);
        const out: { x: number; y: number }[] = [];
        const ekle = (x: number, y: number) => { const l = out[out.length - 1]; if (!l || Math.hypot(l.x - x, l.y - y) > 1e-4) out.push({ x, y }); };
        wexp = new oc.BRepTools_WireExplorer_2(offWire);
        while (wexp.More()) {
          let edge: any = null, curve: any = null;
          try {
            edge = oc.TopoDS.Edge_1(wexp.Current());
            const ters = edge.Orientation?.() === oc.TopAbs_Orientation?.TopAbs_REVERSED;
            curve = new oc.BRepAdaptor_Curve_2(edge);
            const u0 = curve.FirstParameter(), u1 = curve.LastParameter();
            const tip = curve.GetType?.();
            const P = (u: number) => { const p = curve.Value(u); const o2 = { x: p.X(), y: p.Y() }; sil(p); return o2; };
            if (tip === oc.GeomAbs_CurveType?.GeomAbs_Line) {
              const a = P(u0), b = P(u1); if (ters) { ekle(b.x, b.y); ekle(a.x, a.y); } else { ekle(a.x, a.y); ekle(b.x, b.y); }
            } else {   // yay/eğri → ~7.5° örnekle (toolpath noktasal tüketir)
              const N = Math.max(4, Math.min(64, Math.ceil(Math.abs(u1 - u0) / (Math.PI / 24))));
              const pts: { x: number; y: number }[] = []; for (let k = 0; k <= N; k++) pts.push(P(u0 + (u1 - u0) * (k / N)));
              if (ters) pts.reverse();
              for (const q of pts) ekle(q.x, q.y);
            }
          } catch { }
          sil(curve); sil(edge); wexp.Next();
        }
        if (out.length > 2) { const f = out[0], l = out[out.length - 1]; if (Math.hypot(f.x - l.x, f.y - l.y) < 1e-3) out.pop(); }
        return out.length >= 3 ? out : null;
      } catch { return null; } finally { sil(wexp); sil(mo); sil(mp); }
    };
    // Yön garantisi: MakeOffset işareti wire orientasyonuna bağlı → sonucu alanla doğrula (içe=küçülür, dışa=büyür), tersse zıt işaretle tekrar dene
    const A0 = alan2(poly);
    const dogru = (o: { x: number; y: number }[] | null) => !!o && (dIce > 0 ? alan2(o) < A0 : alan2(o) > A0);
    let o = dene(-dIce); if (dogru(o)) return o;
    o = dene(dIce); if (dogru(o)) return o;
    return null;
  }

  /** B-REP YÜZ İÇ KONTURLARI (DELİKLER/ADALAR): yüzün iç wire'larını (OuterWire hariç) döndürür.
   * Her ada bir segment dizisi (LINE/ARC). CAM cep adası (island) otomatik tespiti için. Tarayıcıda doğrulandı. */
  yuzAdalar(shape: any, faceId: number): { tip: 'line' | 'arc' | 'egri'; noktalar: { x: number; y: number; z: number }[]; arc?: { cx: number; cy: number; cz: number; r: number; cw: boolean } }[][] | null {
    const oc = this.oc; if (!oc || !shape) return null;
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const face = this.yuzAl(shape, faceId); if (!face) return null;
    const adalar: any[][] = [];
    let outerW: any = null, we: any = null;
    try {
      outerW = oc.BRepTools.OuterWire(face);
      we = new oc.TopExp_Explorer_2(face, oc.TopAbs_ShapeEnum.TopAbs_WIRE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (we.More()) {
        const wire = oc.TopoDS.Wire_1(we.Current());
        if (!wire.IsSame(outerW)) {            // iç wire = delik/ada
          const seg: any[] = [];
          let wx: any = null;
          try {
            wx = new oc.BRepTools_WireExplorer_2(wire);
            while (wx.More()) {
              let edge: any = null, curve: any = null;
              try {
                edge = oc.TopoDS.Edge_1(wx.Current());
                const ters = edge.Orientation?.() === oc.TopAbs_Orientation?.TopAbs_REVERSED;
                curve = new oc.BRepAdaptor_Curve_2(edge);
                const u0 = curve.FirstParameter(), u1 = curve.LastParameter();
                const tip = curve.GetType?.();
                const P = (u: number) => { const p = curve.Value(u); const o = { x: p.X(), y: p.Y(), z: p.Z() }; sil(p); return o; };
                if (tip === oc.GeomAbs_CurveType?.GeomAbs_Line) {
                  const a = P(u0), b = P(u1); seg.push({ tip: 'line', noktalar: ters ? [b, a] : [a, b] });
                } else if (tip === oc.GeomAbs_CurveType?.GeomAbs_Circle) {
                  const circ = curve.Circle(); const loc = circ.Location(); const cx = loc.X(), cy = loc.Y(), cz = loc.Z(); const r = circ.Radius();
                  const ax = circ.Axis().Direction(); const cw = (ax.Z() < 0) !== ters; const a = P(u0), b = P(u1);
                  seg.push({ tip: 'arc', noktalar: ters ? [b, a] : [a, b], arc: { cx, cy, cz, r, cw } }); sil(loc); sil(ax); sil(circ);
                } else {
                  const N = 16, pts: any[] = []; for (let k = 0; k <= N; k++) pts.push(P(u0 + (u1 - u0) * (k / N))); seg.push({ tip: 'egri', noktalar: ters ? pts.reverse() : pts });
                }
              } catch { }
              sil(curve); sil(edge); wx.Next();
            }
          } finally { sil(wx); }
          if (seg.length) adalar.push(seg);
        }
        we.Next();
      }
    } catch (e) { console.warn('[OCCT] yuzAdalar hata', e); } finally { sil(we); sil(outerW); sil(face); }
    return adalar.length ? adalar : null;
  }

  /** B-REP ANALİTİK SNAP NOKTALARI: ölçüm için GERÇEK köşeler (vertex) + çember/yay merkezleri (+yarıçap) + kenar orta noktaları.
   * Tessellation vertex'i DEĞİL — CATIA seviyesi kesin snap. dedup (pozisyon kuantize). */
  brepSnapNoktalari(shape: any): { tip: 'vertex' | 'merkez' | 'orta'; x: number; y: number; z: number; r?: number }[] {
    const oc = this.oc; if (!oc || !shape) return [];
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const out: { tip: any; x: number; y: number; z: number; r?: number }[] = [];
    const seen = new Set<string>(); const Q = 1e-3; const rd = (v: number) => Math.round(v / Q);
    const ekle = (tip: string, x: number, y: number, z: number, r?: number) => { const k = tip + ':' + rd(x) + ',' + rd(y) + ',' + rd(z); if (!seen.has(k)) { seen.add(k); out.push({ tip: tip as any, x, y, z, r }); } };
    // 1) Gerçek köşeler (TopoDS vertices)
    try {
      const vexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_VERTEX, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (vexp.More()) { try { const v = oc.TopoDS.Vertex_1(vexp.Current()); const p = oc.BRep_Tool.Pnt(v); ekle('vertex', p.X(), p.Y(), p.Z()); sil(p); sil(v); } catch { } vexp.Next(); }
      sil(vexp);
    } catch { }
    // 2) Kenar merkez (çember/yay) + orta nokta
    try {
      const eexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (eexp.More()) {
        try {
          const edge = oc.TopoDS.Edge_1(eexp.Current()); const curve = new oc.BRepAdaptor_Curve_2(edge);
          const u0 = curve.FirstParameter(), u1 = curve.LastParameter();
          if (curve.GetType?.() === oc.GeomAbs_CurveType?.GeomAbs_Circle) { const ci = curve.Circle(); const lo = ci.Location(); ekle('merkez', lo.X(), lo.Y(), lo.Z(), ci.Radius()); sil(lo); sil(ci); }
          if (isFinite(u0) && isFinite(u1)) { const pm = curve.Value((u0 + u1) / 2); ekle('orta', pm.X(), pm.Y(), pm.Z()); sil(pm); }
          sil(curve); sil(edge);
        } catch { } eexp.Next();
      }
      sil(eexp);
    } catch { }
    return out;
  }

  /** B-REP YÜZEY TİPLERİ: TopExp(FACE) sırasıyla (=faceId) her yüzün analitik tipi + merkez/eksen/yarıçap.
   * Ölçüm/snap silindir-eksen + küre merkezi GERÇEK değer kullansın (faceGruplariUret mesh-türevli yerine). */
  brepYuzeyTipleri(shape: any): { tip: string; center?: { x: number; y: number; z: number }; axis?: { x: number; y: number; z: number }; radius?: number }[] {
    const oc = this.oc; if (!oc || !shape) return [];
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    const out: any[] = [];
    try {
      const fexp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      const GA = oc.GeomAbs_SurfaceType;
      while (fexp.More()) {
        let rec: any = { tip: 'OTHER' };
        try {
          const face = oc.TopoDS.Face_1(fexp.Current());
          const surf = new oc.BRepAdaptor_Surface_2(face, false);
          const t = surf.GetType?.();
          if (t === GA?.GeomAbs_Plane) rec.tip = 'PLANE';
          else if (t === GA?.GeomAbs_Cylinder) { const cy = surf.Cylinder(); const ax = cy.Axis(); const lo = ax.Location(); const di = ax.Direction(); rec = { tip: 'CYLINDER', center: { x: lo.X(), y: lo.Y(), z: lo.Z() }, axis: { x: di.X(), y: di.Y(), z: di.Z() }, radius: cy.Radius() }; sil(lo); sil(di); sil(ax); sil(cy); }
          else if (t === GA?.GeomAbs_Sphere) { const sp = surf.Sphere(); const lo = sp.Location(); rec = { tip: 'SPHERE', center: { x: lo.X(), y: lo.Y(), z: lo.Z() }, radius: sp.Radius() }; sil(lo); sil(sp); }
          else if (t === GA?.GeomAbs_Cone) rec.tip = 'CONE';
          sil(surf); sil(face);
        } catch { }
        out.push(rec); fexp.Next();
      }
      sil(fexp);
    } catch { }
    return out;
  }

  /** CATIA Intersect 3D: katı/yüzey ∩ düzlem → GERÇEK kesişim eğrileri (BRepAlgoAPI_Section, gp_Pln).
   * Section sadece iki shape'in ORTAK kesişim kenarlarını verir — kare sınırı/Common hack'i gerekmez. */
  kesitKenarlari(shape: any, origin: { x: number; y: number; z: number }, normal: { x: number; y: number; z: number },
    eksenU?: { x: number; y: number; z: number }, eksenV?: { x: number; y: number; z: number }): { pts: { x: number; y: number; z: number }[] }[] {
    const oc = this.oc; if (!oc || !shape) return [];
    const sil = (o: any) => { try { o?.delete?.(); } catch { } };
    let pnt: any = null, dir: any = null, pln: any = null, sec: any = null;
    try {
      pnt = new oc.gp_Pnt_3(origin.x, origin.y, origin.z); dir = new oc.gp_Dir_4(normal.x, normal.y, normal.z);
      pln = new oc.gp_Pln_3(pnt, dir);
      sec = new oc.BRepAlgoAPI_Section_5(shape, pln, true);   // shape ∩ sonsuz düzlem
      return this.shapeToEdges(sec.Shape());
    } catch (e) { console.warn('[OCCT] kesitKenarlari', e); return []; }
    finally { sil(sec); sil(pln); sil(dir); sil(pnt); }
  }

  /** İki shape'in GERÇEK kesişim eğrileri (BRepAlgoAPI_Section). Combine/Intersect — piyasa kalite B-Rep. */
  kesit(s1: any, s2: any): { pts: { x: number; y: number; z: number }[] }[] {
    const oc = this.oc; if (!oc || !s1 || !s2) return [];
    let sec: any = null;
    try {
      sec = new oc.BRepAlgoAPI_Section_3(s1, s2, true);   // PerformNow=true → kesişim hesaplanır
      const shape = sec.Shape();
      return this.shapeToEdges(shape);
    } catch (e) { console.warn('[OCCT] kesit', e); return []; }
    finally { try { sec?.delete?.(); } catch { } }
  }
  /** Yüzeyleri/shape'leri GERÇEK B-Rep dikişle birleştir (BRepBuilderAPI_Sewing). Join/Sew. */
  dikis(shapes: any[], tol = 1e-3): any {
    const oc = this.oc; if (!oc || !shapes?.length) return null;
    try {
      const sew = new oc.BRepBuilderAPI_Sewing(tol, true, true, true, false);
      for (const s of shapes) if (s) sew.Add(s);
      sew.Perform(new oc.Message_ProgressRange_1());
      return sew.SewedShape();
    } catch (e) { console.warn('[OCCT] dikis', e); return null; }
  }

  /** GSD Healing: ShapeFix ile boşluk/tolerans/yönelim onarımı (kabuk → katı denetimi). */
  iyilestir(shape: any, tol = 0.01): any {
    const oc = this.oc; if (!oc || !shape) return shape;
    try {
      const fix = new oc.ShapeFix_Shape_2(shape);
      fix.SetPrecision(tol); fix.SetMaxTolerance(Math.max(tol * 100, 1));
      fix.Perform(new oc.Message_ProgressRange_1());
      return fix.Shape();
    } catch (e) { try { const u = new oc.ShapeUpgrade_UnifySameDomain_2(shape, true, true, false); u.Build(); return u.Shape(); } catch (e2) { return shape; } }
  }
  /** GSD Untrim: bir yüzün altındaki tam (doğal sınırlı) yüzeyi geri getir. */
  untrimYuzey(face: any): any {
    const oc = this.oc; if (!oc || !face) return face;
    try {
      const sH = oc.BRep_Tool.Surface_2(face);
      return new oc.BRepBuilderAPI_MakeFace_8(sH, 1e-6).Face();
    } catch (e) { return face; }
  }
  /** GSD Disassemble: bir shape'i bağımsız yüzlerine ayır (her biri ayrı yüzey). */
  parcala(shape: any): any[] { return this.yuzler(shape); }
  /** GSD Surface Simplification: aynı-domain yüz/kenarları birleştir (sadeleştir). */
  sadelestir(shape: any): any {
    const oc = this.oc; if (!oc || !shape) return shape;
    try { const u = new oc.ShapeUpgrade_UnifySameDomain_2(shape, true, true, false); u.Build(); return u.Shape(); } catch (e) { return shape; }
  }

  // ---- Sketch → katı (gerçek B-Rep pad/pocket) ----
  /** Kapalı profil noktalarından (dünya 3D) wire→face→prism (extrude `yon` vektörü kadar). */
  makePrism(noktalar: { x: number; y: number; z: number }[], yon: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    const face = new oc.BRepBuilderAPI_MakeFace_15(this.wire(noktalar), false).Face();
    return new oc.BRepPrimAPI_MakePrism_1(face, new oc.gp_Vec_4(yon.x, yon.y, yon.z), false, true).Shape();
  }

  /** Nokta dizisi GERÇEK çember mi? (sabit yarıçap, ≥12 nokta) → {merkez,normal,r} veya null. */
  private cemberTespit(pts: { x: number; y: number; z: number }[]): { merkez: any; normal: any; r: number } | null {
    const n = pts.length; if (n < 12) return null;
    let cx = 0, cy = 0, cz = 0; for (const p of pts) { cx += p.x; cy += p.y; cz += p.z; } cx /= n; cy /= n; cz /= n;
    let rMin = Infinity, rMax = 0, rSum = 0; for (const p of pts) { const r = Math.hypot(p.x - cx, p.y - cy, p.z - cz); if (r < rMin) rMin = r; if (r > rMax) rMax = r; rSum += r; }
    const r = rSum / n; if (r < 1e-4 || rMax - rMin > r * 0.03) return null;   // sabit yarıçap değil → poligon
    const a = pts[0], b = pts[Math.floor(n / 3)], c = pts[Math.floor((2 * n) / 3)];
    const ux = b.x - a.x, uy = b.y - a.y, uz = b.z - a.z, vx = c.x - a.x, vy = c.y - a.y, vz = c.z - a.z;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const nl = Math.hypot(nx, ny, nz); if (nl < 1e-6) return null; nx /= nl; ny /= nl; nz /= nl;
    return { merkez: { x: cx, y: cy, z: cz }, normal: { x: nx, y: ny, z: nz }, r };
  }

  /** GERÇEK daire wire (gp_Circ edge) — pocket/pad çemberlerinde poligon yerine pürüzsüz silindir. */
  private wireCember(c: { merkez: any; normal: any; r: number }): any {
    const oc = this.oc;
    const ax = new oc.gp_Ax2_3(new oc.gp_Pnt_3(c.merkez.x, c.merkez.y, c.merkez.z), new oc.gp_Dir_4(c.normal.x, c.normal.y, c.normal.z));
    const circ = new oc.gp_Circ_2(ax, c.r);
    const edge = new oc.BRepBuilderAPI_MakeEdge_8(circ).Edge();
    return new oc.BRepBuilderAPI_MakeWire_2(edge).Wire();
  }

  /** Wire üretir — GERÇEK çemberse daire edge, değilse kapalı polygon. */
  private wire(noktalar: { x: number; y: number; z: number }[]): any {
    const oc = this.oc;
    const cc = this.cemberTespit(noktalar);
    if (cc) { try { return this.wireCember(cc); } catch (e) { console.warn('[OCCT] çember wire hatası, poligona düşülüyor', e); } }
    const poly = new oc.BRepBuilderAPI_MakePolygon_1();
    for (const p of noktalar) poly.Add_1(new oc.gp_Pnt_3(p.x, p.y, p.z));
    poly.Close();
    return poly.Wire();
  }

  /** Delikli profil prism: dış kontur CCW + iç delikler CW (ters yön) → delikli katı.
   * NOT: nokta orientasyonu (dış CCW, delik CW) çağıran tarafından garantilenmeli. */
  makePrismDelikli(disPts: { x: number; y: number; z: number }[], delikler: { x: number; y: number; z: number }[][], yon: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    const mf = new oc.BRepBuilderAPI_MakeFace_15(this.wire(disPts), false);
    for (const d of delikler) if (d && d.length >= 3) mf.Add(this.wire(d));
    return new oc.BRepPrimAPI_MakePrism_1(mf.Face(), new oc.gp_Vec_4(yon.x, yon.y, yon.z), false, true).Shape();
  }

  /** Revolve (torna/shaft/groove): profil dış kontur (+delikler) bir eksen etrafında döndürülür. aci rad (2π=tam). */
  makeRevolve(disPts: { x: number; y: number; z: number }[], delikler: { x: number; y: number; z: number }[][], eksenNokta: { x: number; y: number; z: number }, eksenYon: { x: number; y: number; z: number }, aci = Math.PI * 2): any {
    const oc = this.oc;
    const mf = new oc.BRepBuilderAPI_MakeFace_15(this.wire(disPts), false);
    for (const d of (delikler || [])) if (d && d.length >= 3) mf.Add(this.wire(d));
    const eksen = new oc.gp_Ax1_2(new oc.gp_Pnt_3(eksenNokta.x, eksenNokta.y, eksenNokta.z), new oc.gp_Dir_4(eksenYon.x, eksenYon.y, eksenYon.z));
    return new oc.BRepPrimAPI_MakeRevol_1(mf.Face(), eksen, aci, false).Shape();
  }

  /** Açık wire (yol/path için — kapatılmaz). */
  private wireAcik(noktalar: { x: number; y: number; z: number }[]): any {
    const oc = this.oc;
    const poly = new oc.BRepBuilderAPI_MakePolygon_1();
    for (const p of noktalar) poly.Add_1(new oc.gp_Pnt_3(p.x, p.y, p.z));
    return poly.Wire();
  }

  /** LOFT (ThruSections): birden çok kesit profili arasında pürüzsüz katı (kanat/huni/geçiş). */
  makeLoft(profiller: { x: number; y: number; z: number }[][], solid = true): any {
    const oc = this.oc;
    const ts = new oc.BRepOffsetAPI_ThruSections(solid, false, 1e-6);   // isSolid, ruled=false (pürüzsüz), tol
    let n = 0;
    for (const p of profiller) if (p && p.length >= 3) { ts.AddWire(this.wire(p)); n++; }
    if (n < 2) return null;   // loft için en az 2 kesit
    ts.Build();
    return ts.Shape();
  }

  /** BLEND (iki eğri arası yüzey): açık profiller arası pürüzsüz shell (ThruSections solid=false). */
  makeBlend(p1: { x: number; y: number; z: number }[], p2: { x: number; y: number; z: number }[]): any {
    const oc = this.oc;
    if (!p1 || p1.length < 2 || !p2 || p2.length < 2) return null;
    try {
      const ts = new oc.BRepOffsetAPI_ThruSections(false, false, 1e-6);   // isSolid=false → açık yüzey (shell)
      ts.AddWire(this.wireAcik(p1)); ts.AddWire(this.wireAcik(p2));
      ts.Build();
      return ts.Shape();
    } catch (e) { console.warn('[OCCT] makeBlend', e); return null; }
  }

  /** EXTRAPOLATE: yüzeyi 4 yönde d kadar uzat (NurbsConvert→BoundedSurface→GeomLib.ExtendSurfByLength→MakeFace). */
  yuzeyUzat(shape: any, d: number): any {
    const oc = this.oc; if (!oc || !shape || !(d > 0)) return null;
    try {
      const face = this.yuzler(shape)[0]; if (!face) return null;
      const nf = new oc.BRepBuilderAPI_NurbsConvert_2(face, true).Shape();   // yüzeyi BSpline'a çevir (bounded)
      const nface = this.yuzler(nf)[0]; if (!nface) return null;
      const surfH = oc.BRep_Tool.Surface_2(nface);
      const bsB = new oc.Handle_Geom_BoundedSurface_2(surfH.get());          // BoundedSurface handle
      oc.GeomLib.ExtendSurfByLength(bsB, d, 1, true, true);
      oc.GeomLib.ExtendSurfByLength(bsB, d, 1, false, true);
      oc.GeomLib.ExtendSurfByLength(bsB, d, 1, true, false);
      oc.GeomLib.ExtendSurfByLength(bsB, d, 1, false, false);
      const sH = new oc.Handle_Geom_Surface_2(bsB.get());
      return new oc.BRepBuilderAPI_MakeFace_8(sH, 1e-6).Face();
    } catch (e) { console.warn('[OCCT] yuzeyUzat', e); return null; }
  }

  // ───────── GSD YÜZEY OLUŞTURMA (Surface — açık shell, hacimsiz) ─────────
  /** Yüzey Extrude: profil wire'ını yön boyunca uzat → shell (ruled surface). */
  makeYuzeyExtrude(profil3d: { x: number; y: number; z: number }[], vec: { x: number; y: number; z: number }, kapali = false): any {
    const oc = this.oc;
    const wire = kapali ? this.wire(profil3d) : this.wireAcik(profil3d);
    return new oc.BRepPrimAPI_MakePrism_1(wire, new oc.gp_Vec_4(vec.x, vec.y, vec.z), false, true).Shape();
  }
  /** Yüzey Revolve: profil wire'ını eksen etrafında döndür → shell. */
  makeYuzeyRevolve(profil3d: { x: number; y: number; z: number }[], origin: { x: number; y: number; z: number }, eksen: { x: number; y: number; z: number }, aci: number, kapali = false): any {
    const oc = this.oc;
    const wire = kapali ? this.wire(profil3d) : this.wireAcik(profil3d);
    const ax = new oc.gp_Ax1_2(new oc.gp_Pnt_3(origin.x, origin.y, origin.z), new oc.gp_Dir_4(eksen.x, eksen.y, eksen.z));
    return new oc.BRepPrimAPI_MakeRevol_1(wire, ax, aci, false).Shape();
  }
  /** Yüzey Fill: kapalı profil → düzlemsel dolgu yüzeyi (tek face). */
  makeYuzeyFill(profil3d: { x: number; y: number; z: number }[]): any {
    const oc = this.oc;
    return new oc.BRepBuilderAPI_MakeFace_15(this.wire(profil3d), false).Face();
  }
  /** Yüzey Sweep: profil wire'ını yol boyunca süpür → shell (MakePipe wire üstünde). */
  makeYuzeySweep(profil3d: { x: number; y: number; z: number }[], yol3d: { x: number; y: number; z: number }[], kapali = false): any {
    const oc = this.oc;
    if (!profil3d || profil3d.length < 2 || !yol3d || yol3d.length < 2) return null;
    const profWire = kapali ? this.wire(profil3d) : this.wireAcik(profil3d);
    const yolWire = yol3d.length >= 3 ? this.smoothWire(yol3d) : this.wireAcik(yol3d);   // pürüzsüz B-spline yol (faset önler)
    const pipe = new oc.BRepOffsetAPI_MakePipe_1(yolWire, profWire);
    try { pipe.Build(new oc.Message_ProgressRange_1()); } catch (e) { }
    return pipe.Shape();
  }
  /** Thick Surface: shell'i t kalınlaştır → KATI (yüzey→solid köprüsü). Arc join (shell için sağlam). */
  yuzeyKalinlik(shell: any, t: number, _t2 = 0): any {
    const oc = this.oc;
    const list = new oc.TopTools_ListOfShape_1();
    const mts = new oc.BRepOffsetAPI_MakeThickSolid_1();
    mts.MakeThickSolidByJoin(shell, list, Math.abs(t) + Math.abs(_t2), 1e-3, oc.BRepOffset_Mode.BRepOffset_Skin, false, false, oc.GeomAbs_JoinType.GeomAbs_Arc, false);
    let s: any = null; try { s = mts.Shape(); } catch (e) { return null; }
    try { const props = new oc.GProp_GProps_1(); oc.BRepGProp.VolumeProperties_1(s, props, false, false, false); if (props.Mass() < 0) s = s.Reversed(); } catch (e) { }
    return s;
  }

  /** Noktalardan PÜRÜZSÜZ B-spline wire (kesit süpürme yolu için — polyline yerine). */
  smoothWire(pts: { x: number; y: number; z: number }[]): any {
    const oc = this.oc;
    if (!pts || pts.length < 3) return this.wireAcik(pts);
    try {
      const arr = new oc.TColgp_Array1OfPnt_2(1, pts.length);
      for (let i = 0; i < pts.length; i++) arr.SetValue(i + 1, new oc.gp_Pnt_3(pts[i].x, pts[i].y, pts[i].z));
      const ptb = new oc.GeomAPI_PointsToBSpline_2(arr, 3, 8, oc.GeomAbs_Shape.GeomAbs_C2, 1e-3);
      const bs = ptb.Curve();   // Handle_Geom_BSplineCurve
      const ch = new oc.Handle_Geom_Curve_2(bs.get());
      const edge = new oc.BRepBuilderAPI_MakeEdge_24(ch).Edge();
      return new oc.BRepBuilderAPI_MakeWire_2(edge).Wire();
    } catch (e) { console.warn('[OCCT] smoothWire fallback', e); return this.wireAcik(pts); }
  }
  /** SWEEP (MakePipe): profili bir yol (path) boyunca süpür (boru/kanal/kablo kanalı). smooth=true → B-spline yol (pürüzsüz). */
  makeSweep(profil: { x: number; y: number; z: number }[], yol: { x: number; y: number; z: number }[], smooth = false): any {
    const oc = this.oc;
    if (!profil || profil.length < 3 || !yol || yol.length < 2) return null;
    const profFace = new oc.BRepBuilderAPI_MakeFace_15(this.wire(profil), false).Face();
    const yolWire = smooth ? this.smoothWire(yol) : this.wireAcik(yol);
    const pipe = new oc.BRepOffsetAPI_MakePipe_1(yolWire, profFace);
    try { pipe.Build(new oc.Message_ProgressRange_1()); } catch (e) { }
    return pipe.Shape();
  }

  // ---- Primitif shape üreticiler (OCCT B-Rep) ----
  makeBox(dx: number, dy: number, dz: number): any { return new this.oc.BRepPrimAPI_MakeBox_2(dx, dy, dz).Shape(); }
  makeCylinder(r: number, h: number): any { return new this.oc.BRepPrimAPI_MakeCylinder_1(r, h).Shape(); }
  makeSphere(r: number): any { return new this.oc.BRepPrimAPI_MakeSphere_1(r).Shape(); }
  makeCone(r1: number, r2: number, h: number): any { return new this.oc.BRepPrimAPI_MakeCone_1(r1, r2, h).Shape(); }
  /** Shape'i yerel→dünya düzlemine taşır (eksenU/eksenV/normal bazları + origin). */
  duzlemeYerlestir(shape: any, o: { x: number; y: number; z: number }, u: { x: number; y: number; z: number }, v: { x: number; y: number; z: number }, n: { x: number; y: number; z: number }): any {
    const oc = this.oc; const t = new oc.gp_Trsf_1();
    try { t.SetValues(u.x, v.x, n.x, o.x, u.y, v.y, n.y, o.y, u.z, v.z, n.z, o.z); } catch (e) { return shape; }
    return new oc.BRepBuilderAPI_Transform_2(shape, t, false).Shape();
  }
  /** OCCT Torus primitifi (R1=ana yarıçap, R2=boru yarıçapı). */
  makeTorus(r1: number, r2: number): any { return new this.oc.BRepPrimAPI_MakeTorus_1(r1, r2).Shape(); }
  /** OCCT Wedge (kama) primitifi: dx×dy×dz kutu, üst yüz x'te ltx'e daralır. */
  makeWedge(dx: number, dy: number, dz: number, ltx: number): any {
    try { return new this.oc.BRepPrimAPI_MakeWedge_2(dx, dy, dz, ltx).Shape(); }
    catch (e) { return this.makeBox(dx, dy, dz); }
  }
  /** OCCT 3D Offset Shape: tüm katıyı d kadar dışa(+)/içe(−) ötele (kalınlaştırmadan büyüt/küçült). */
  offsetSolid(shape: any, d: number, tol = 0.01): any {
    const oc = this.oc; if (!shape || Math.abs(d) < 1e-6) return shape;
    try {
      const off = new oc.BRepOffsetAPI_MakeOffsetShape();
      off.PerformBySimple(shape, d);
      if (off.IsDone()) return off.Shape();
    } catch (e) { }
    try {
      const off2 = new oc.BRepOffsetAPI_MakeOffsetShape_1();
      off2.PerformBySimple(shape, d); if (off2.IsDone()) return off2.Shape();
    } catch (e) { }
    return shape;
  }
  /** OCCT kütle özellikleri: hacim, yüzey alanı, ağırlık merkezi. */
  kutleOzellikleri(shape: any): { hacim: number; alan: number; merkez: { x: number; y: number; z: number } } {
    const oc = this.oc; let hacim = 0, alan = 0, mx = 0, my = 0, mz = 0;
    try { const gv = new oc.GProp_GProps_1(); oc.BRepGProp.VolumeProperties_1(shape, gv, false, false, false); hacim = Math.abs(gv.Mass()); const cm = gv.CentreOfMass(); mx = cm.X(); my = cm.Y(); mz = cm.Z(); } catch (e) { }
    try { const gs = new oc.GProp_GProps_1(); oc.BRepGProp.SurfaceProperties_1(shape, gs, false, false); alan = gs.Mass(); } catch (e) { }
    return { hacim, alan, merkez: { x: mx, y: my, z: mz } };
  }
  /** OCCT hassas min mesafe (BRepExtrema_DistShapeShape) — iki shape arası en kısa mesafe + uç noktalar. */
  minMesafe(s1: any, s2: any): { mesafe: number; p1: { x: number; y: number; z: number }; p2: { x: number; y: number; z: number } } | null {
    const oc = this.oc;
    try {
      let ext: any;
      try { ext = new oc.BRepExtrema_DistShapeShape_1(); ext.LoadS1(s1); ext.LoadS2(s2); ext.Perform(); }
      catch (e1) { try { ext = new oc.BRepExtrema_DistShapeShape_2(s1, s2, oc.Extrema_ExtFlag.Extrema_ExtFlag_MINMAX, oc.Extrema_ExtAlgo.Extrema_ExtAlgo_Grad); } catch (e2) { ext = new oc.BRepExtrema_DistShapeShape_3(s1, s2, 1e-6, oc.Extrema_ExtFlag.Extrema_ExtFlag_MINMAX, oc.Extrema_ExtAlgo.Extrema_ExtAlgo_Grad); } }
      if (!ext.IsDone() || ext.NbSolution() < 1) return null;
      const a = ext.PointOnShape1(1), b = ext.PointOnShape2(1);
      return { mesafe: ext.Value(), p1: { x: a.X(), y: a.Y(), z: a.Z() }, p2: { x: b.X(), y: b.Y(), z: b.Z() } };
    } catch (e) { return null; }
  }
  /** OCCT geometri geçerlilik kontrolü (BRepCheck_Analyzer). */
  gecerliMi(shape: any): boolean {
    try { return new this.oc.BRepCheck_Analyzer(shape, true, false).IsValid(); } catch (e) { return true; }
  }
  /** OCCT sınır kutusu (AABB) — min/maks köşe + boyut. */
  sinirKutu(shape: any): { min: { x: number; y: number; z: number }; maks: { x: number; y: number; z: number }; boyut: { x: number; y: number; z: number } } | null {
    const oc = this.oc;
    try {
      const box = new oc.Bnd_Box_1();
      try { oc.BRepBndLib.AddOptimal(shape, box, true, false); } catch (e) { oc.BRepBndLib.Add(shape, box, false); }
      const cmin = box.CornerMin(), cmax = box.CornerMax();
      const mn = { x: cmin.X(), y: cmin.Y(), z: cmin.Z() }, mx = { x: cmax.X(), y: cmax.Y(), z: cmax.Z() };
      return { min: mn, maks: mx, boyut: { x: mx.x - mn.x, y: mx.y - mn.y, z: mx.z - mn.z } };
    } catch (e) { return null; }
  }
  /** GSD Yüzey Küre: küresel kabuk (tek yüzey, kapalı). */
  kureYuzey(r: number): any {
    const sh = this.makeSphere(r);
    const f = this.yuzler(sh); return f.length ? f[0] : sh;
  }
  /** GSD Yüzey Silindir: yalnızca yan (silindirik) yüzey — eksen merkez/yön ile yerleştirilir. */
  silindirYanYuzey(merkez: { x: number; y: number; z: number }, yon: { x: number; y: number; z: number }, r: number, h: number): any {
    const kati = this.makeSilindirEksen(merkez, yon, r, h);
    const yuzler = this.yuzler(kati);
    // düzlemsel olmayan (silindirik) yüzü seç: alanı 2πrh'ye en yakın
    const hedef = 2 * Math.PI * r * h; let best = null, bestFark = Infinity;
    for (const fc of yuzler) { try { const a = this.yuzAlan(fc); const fark = Math.abs(a - hedef); if (fark < bestFark) { bestFark = fark; best = fc; } } catch (e) { } }
    return best || kati;
  }
  /** Yüzey alanı (GProp). */
  yuzAlan(face: any): number {
    const oc = this.oc; const g = new oc.GProp_GProps_1();
    oc.BRepGProp.SurfaceProperties_1(face, g, false, false); return g.Mass();
  }

  // ---- Boolean (gerçek B-Rep) — Fuse/Cut/Common_3 = (Shape, Shape) 2 arg ----
  // builder saklanır → aktif kimlik varsa OCCT history ile stableId taşınır (persistent naming).
  birlestir(a: any, b: any): any { const op = new this.oc.BRepAlgoAPI_Fuse_3(a, b); const s = op.Shape(); return this.kimlikGecis(op, a, s); }
  cikar(a: any, b: any): any { const op = new this.oc.BRepAlgoAPI_Cut_3(a, b); const s = op.Shape(); return this.kimlikGecis(op, a, s); }
  kesistir(a: any, b: any): any { const op = new this.oc.BRepAlgoAPI_Common_3(a, b); const s = op.Shape(); return this.kimlikGecis(op, a, s); }

  // ---- Dönüşümler (mirror / kopya — gp_Trsf + BRepBuilderAPI_Transform) ----
  /** Shape'i temel düzleme (xy/xz/yz) aynalar. */
  mirror(shape: any, duzlem: 'xy' | 'xz' | 'yz'): any {
    const oc = this.oc;
    const dir = duzlem === 'yz' ? new oc.gp_Dir_4(1, 0, 0) : duzlem === 'xz' ? new oc.gp_Dir_4(0, 1, 0) : new oc.gp_Dir_4(0, 0, 1);
    const trsf = new oc.gp_Trsf_1(); trsf.SetMirror_3(new oc.gp_Ax2_3(new oc.gp_Pnt_3(0, 0, 0), dir));
    return this.kimlikTransformTasi(shape, new oc.BRepBuilderAPI_Transform_2(shape, trsf, false).Shape());
  }
  /** Shape'in öteleneнmiş kopyası (pattern için). */
  oteleKopya(shape: any, dx: number, dy: number, dz: number): any {
    const oc = this.oc;
    const trsf = new oc.gp_Trsf_1(); trsf.SetTranslation_1(new oc.gp_Vec_4(dx, dy, dz));
    return this.kimlikTransformTasi(shape, new oc.BRepBuilderAPI_Transform_2(shape, trsf, false).Shape());
  }
  /** Shape'i bir merkez etrafında k katı ölçekler (Scaling). */
  olcekle(shape: any, merkez: { x: number; y: number; z: number }, k: number): any {
    const oc = this.oc;
    const trsf = new oc.gp_Trsf_1(); trsf.SetScale(new oc.gp_Pnt_3(merkez.x, merkez.y, merkez.z), k);
    return this.kimlikTransformTasi(shape, new oc.BRepBuilderAPI_Transform_2(shape, trsf, false).Shape());
  }
  /** CATIA Affinity: eksen-bazlı orantısız ölçek (gp_GTrsf köşegen kx/ky/kz). merkez verilmezse origin. */
  afinDonusum(shape: any, kx: number, ky: number, kz: number, merkez?: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    const m = merkez || { x: 0, y: 0, z: 0 };
    const g = new oc.gp_GTrsf_1();
    g.SetValue(1, 1, kx); g.SetValue(2, 2, ky); g.SetValue(3, 3, kz);
    // merkez etrafında: T(m) · S · T(-m) → çeviri sütununa (I-S)·m
    g.SetValue(1, 4, m.x * (1 - kx)); g.SetValue(2, 4, m.y * (1 - ky)); g.SetValue(3, 4, m.z * (1 - kz));
    return this.kimlikTransformTasi(shape, new oc.BRepBuilderAPI_GTransform_2(shape, g, false).Shape());
  }
  /** Shape'in eksen (merkez+yön) etrafında döndürülmüş kopyası (dairesel pattern). merkez verilmezse origin. */
  dondurKopya(shape: any, eksenYon: { x: number; y: number; z: number }, aciRad: number, merkez?: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    const trsf = new oc.gp_Trsf_1();
    const m = merkez || { x: 0, y: 0, z: 0 };
    trsf.SetRotation_1(new oc.gp_Ax1_2(new oc.gp_Pnt_3(m.x, m.y, m.z), new oc.gp_Dir_4(eksenYon.x, eksenYon.y, eksenYon.z)), aciRad);
    return this.kimlikTransformTasi(shape, new oc.BRepBuilderAPI_Transform_2(shape, trsf, false).Shape());
  }

  /** Z-eksenli (origin) shape'i hedef merkez+yön eksenine yerleştirir (delik/koni araçları için).
   * Rotasyon (Z→yön, kanıtlı SetRotation) + öteleme — gp_Ax3 binding'ine bağımlı değil. */
  ekseneYerlestir(shape: any, merkez: { x: number; y: number; z: number }, yon: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    const L = Math.hypot(yon.x, yon.y, yon.z) || 1; const nz = yon.z / L;
    let placed = shape;
    const dot = Math.max(-1, Math.min(1, nz));   // Z·n
    if (dot < 0.999999) {
      let ax = { x: -yon.y / L, y: yon.x / L, z: 0 };   // cross(Z, n) = (-ny, nx, 0)
      let al = Math.hypot(ax.x, ax.y, ax.z);
      if (al < 1e-6) { ax = { x: 1, y: 0, z: 0 }; al = 1; }   // n ≈ -Z → X ekseninde 180°
      const trsf = new oc.gp_Trsf_1();
      trsf.SetRotation_1(new oc.gp_Ax1_2(new oc.gp_Pnt_3(0, 0, 0), new oc.gp_Dir_4(ax.x / al, ax.y / al, ax.z / al)), Math.acos(dot));
      placed = new oc.BRepBuilderAPI_Transform_2(placed, trsf, false).Shape();
    }
    return this.oteleKopya(placed, merkez.x, merkez.y, merkez.z);
  }
  /** Delik aracı silindiri: merkez noktadan 'yon' boyunca r yarıçap, h derinlik (herhangi eksen, circle-wire-prism). */
  makeSilindirEksen(merkez: { x: number; y: number; z: number }, yon: { x: number; y: number; z: number }, r: number, h: number): any {
    const oc = this.oc;
    const ax = new oc.gp_Ax2_3(new oc.gp_Pnt_3(merkez.x, merkez.y, merkez.z), new oc.gp_Dir_4(yon.x, yon.y, yon.z));
    const circ = new oc.gp_Circ_2(ax, r);
    const edge = new oc.BRepBuilderAPI_MakeEdge_8(circ).Edge();
    const wire = new oc.BRepBuilderAPI_MakeWire_2(edge).Wire();
    const face = new oc.BRepBuilderAPI_MakeFace_15(wire, false).Face();
    return new oc.BRepPrimAPI_MakePrism_1(face, new oc.gp_Vec_4(yon.x * h, yon.y * h, yon.z * h), false, true).Shape();
  }
  /** Koni aracı (havşa/V-dip): merkez+yön ekseninde taban r1, tepe r2, yükseklik h. */
  makeKoniEksen(merkez: { x: number; y: number; z: number }, yon: { x: number; y: number; z: number }, r1: number, r2: number, h: number): any {
    return this.ekseneYerlestir(this.makeCone(r1, r2, h), merkez, yon);
  }

  /** KUSURSUZ fillet: shape'in TÜM kenarlarına (veya verilen edge listesine) r yarıçap. */
  fillet(shape: any, r: number, edges?: any[]): any {
    const oc = this.oc;
    const mf = new oc.BRepFilletAPI_MakeFillet(shape, oc.ChFi3d_FilletShape.ChFi3d_Rational);
    if (edges?.length) { for (const e of edges) mf.Add_2(r, e); }
    else { const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE); while (exp.More()) { mf.Add_2(r, oc.TopoDS.Edge_1(exp.Current())); exp.Next(); } }
    // r kenar yarıçapından büyükse OCCT başarısız → Shape() throw eder; sessizce null dön (çağıran eski shape'i korur + uyarır)
    try { const s = mf.Shape(); if (mf.IsDone && !mf.IsDone()) return null; return this.kimlikGecis(mf, shape, s); } catch (e) { console.warn('[OCCT fillet] başarısız (yarıçap çok büyük olabilir)', e); return null; }
  }

  /** DEĞİŞKEN yarıçaplı fillet (CATIA Variable Fillet): seçili kenarlarda r1→r2 doğrusal. Add_3 bind yoksa null. */
  filletDegisken(shape: any, r1: number, r2: number, edges: any[]): any {
    const oc = this.oc;
    if (!edges?.length) return null;
    try {
      const mf = new oc.BRepFilletAPI_MakeFillet(shape, oc.ChFi3d_FilletShape.ChFi3d_Rational);
      for (const e of edges) mf.Add_3(r1, r2, e);   // Radius1→Radius2 kenar boyunca
      const s = mf.Shape(); if (mf.IsDone && !mf.IsDone()) return null; return this.kimlikGecis(mf, shape, s);   // başarısız → null (çağıran sabit fillet'e düşer)
    } catch (e) { console.warn('[OCCT filletDegisken] başarısız', e); return null; }
  }

  /** KUSURSUZ chamfer: shape kenarlarına mesafe. */
  chamfer(shape: any, mesafe: number, edges?: any[]): any {
    const oc = this.oc;
    const mc = new oc.BRepFilletAPI_MakeChamfer(shape);
    // Add_2(Dis, Edge) = mesafeli pah (Add_1 sadece Edge ekler, mesafe=0 → pah kırmaz)
    if (edges?.length) { for (const e of edges) mc.Add_2(mesafe, e); }
    else { const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE); while (exp.More()) { mc.Add_2(mesafe, oc.TopoDS.Edge_1(exp.Current())); exp.Next(); } }
    // mesafe çok büyükse OCCT başarısız → Shape() throw eder; sessizce null dön (çağıran eski shape'i korur + uyarır)
    try { const s = mc.Shape(); if (mc.IsDone && !mc.IsDone()) return null; return this.kimlikGecis(mc, shape, s); } catch (e) { console.warn('[OCCT chamfer] başarısız (mesafe çok büyük olabilir)', e); return null; }
  }

  /** Shape'in kenarlarını dizi olarak döndürür (edge seçimi için). */
  kenarlar(shape: any): any[] {
    const oc = this.oc; const out: any[] = [];
    const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More()) { out.push(oc.TopoDS.Edge_1(exp.Current())); exp.Next(); }
    return out;
  }

  /** OCCT edge'in orta noktası (2 uç vertex ortalaması) — mesh seçim eşleştirme için. */
  edgeOrtaNokta(edge: any): { x: number; y: number; z: number } {
    const oc = this.oc;
    const v1 = new oc.TopoDS_Vertex(), v2 = new oc.TopoDS_Vertex();
    oc.TopExp.Vertices_1(edge, v1, v2, false);
    const p1 = oc.BRep_Tool.Pnt(v1), p2 = oc.BRep_Tool.Pnt(v2);
    return { x: (p1.X() + p2.X()) / 2, y: (p1.Y() + p2.Y()) / 2, z: (p1.Z() + p2.Z()) / 2 };
  }

  /** Seçili dünya noktalarına yakın OCCT edge'leri (mesh seçim → OCCT topoloji eşleştirme). */
  seciliEdgeler(shape: any, secNoktalar: { x: number; y: number; z: number }[], tol = 6): any[] {
    // Her seçili nokta için YALNIZCA en yakın TEK edge'i eşle (2 seçim → en çok 2 edge).
    // Eski "tol içindeki tüm edge'ler" yaklaşımı kümelenmiş midpoint'lerde 1 seçimi 7+ edge'e yayıyordu.
    const tol2 = tol * tol;
    const kenarlar = this.kenarlar(shape);
    // Kenarı polyline'a örnekle, seçili noktanın kenara mesafesini NOKTA-SEGMENT olarak ölç.
    // (Sadece köşe noktalarına bakmak UZUN kenarda hatalıydı: 680mm kenar 9 örnekle ~85mm aralık →
    //  kenar üstündeki seçim iki örnek arasına düşüp ~42mm uzak görünüyordu. Segment mesafesi kesin.)
    const polyler = kenarlar.map(e => this.edgeNoktalari(e, 16));
    const out: any[] = []; const alinan = new Set<number>();
    for (const p of secNoktalar) {
      let best = -1, bestD = tol2;
      for (let i = 0; i < kenarlar.length; i++) {
        if (alinan.has(i)) continue;
        const poly = polyler[i]; let dmin = Infinity;
        for (let k = 1; k < poly.length; k++) { const d = this.noktaSegmentMesafe2(p, poly[k - 1], poly[k]); if (d < dmin) dmin = d; }
        if (poly.length === 1) { const m = poly[0], dx = m.x - p.x, dy = m.y - p.y, dz = m.z - p.z; dmin = dx * dx + dy * dy + dz * dz; }
        if (dmin < bestD) { bestD = dmin; best = i; }
      }
      if (best >= 0) { alinan.add(best); out.push(kenarlar[best]); }
    }
    return out;
  }

  /** Noktanın [a,b] segmentine KARESEL en yakın mesafesi (kenar eşleştirme: uzun kenarda da kesin). */
  private noktaSegmentMesafe2(p: { x: number; y: number; z: number }, a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
    const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
    const apx = p.x - a.x, apy = p.y - a.y, apz = p.z - a.z;
    const ab2 = abx * abx + aby * aby + abz * abz;
    let t = ab2 > 1e-9 ? (apx * abx + apy * aby + apz * abz) / ab2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const dx = apx - abx * t, dy = apy - aby * t, dz = apz - abz * t;
    return dx * dx + dy * dy + dz * dz;
  }

  /** OCCT edge'i eğri boyunca N+1 noktada örnekler (seçim eşleştirme: noktaya en yakın kenar). */
  edgeNoktalari(edge: any, N = 9): { x: number; y: number; z: number }[] {
    const oc = this.oc; const out: { x: number; y: number; z: number }[] = [];
    try {
      const cur = new oc.BRepAdaptor_Curve_2(edge);
      const u0 = cur.FirstParameter(), u1 = cur.LastParameter();
      if (isFinite(u0) && isFinite(u1) && u1 > u0) {
        for (let i = 0; i <= N; i++) { const p = cur.Value(u0 + (u1 - u0) * (i / N)); out.push({ x: p.X(), y: p.Y(), z: p.Z() }); try { p.delete(); } catch { } }
      }
      try { cur.delete(); } catch { }
    } catch { }
    if (!out.length) out.push(this.edgeOrtaNokta(edge));
    return out;
  }

  /** KUSURSUZ draft (eğim/koni açısı): seçili yüzleri çekme yönüne göre açıyla eğer (kalıp çıkış açısı). */
  draft(shape: any, aciDeg: number, faces: any[], pullYon: { x: number; y: number; z: number }, neutralNokta: { x: number; y: number; z: number }): any {
    const oc = this.oc;
    if (!faces || !faces.length) return shape;
    const da = new oc.BRepOffsetAPI_DraftAngle_2(shape);
    const pull = new oc.gp_Dir_4(pullYon.x, pullYon.y, pullYon.z);
    const neutral = new oc.gp_Pln_3(new oc.gp_Pnt_3(neutralNokta.x, neutralNokta.y, neutralNokta.z), new oc.gp_Dir_4(pullYon.x, pullYon.y, pullYon.z));
    const aci = aciDeg * Math.PI / 180;
    let eklendi = 0;
    for (const f of faces) { try { da.Add(f, pull, aci, neutral, true); eklendi++; } catch (e) { /* pull'a paralel yüz eğilemez */ } }
    if (!eklendi) return shape;
    try { da.Build(); return da.IsDone() ? da.Shape() : shape; } catch (e) { return shape; }
  }

  /** Shape'in yüzlerini dizi olarak döndürür (yüz seçimi için). */
  yuzler(shape: any): any[] {
    const oc = this.oc; const out: any[] = [];
    const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More()) { out.push(oc.TopoDS.Face_1(exp.Current())); exp.Next(); }
    return out;
  }

  /** OCCT yüzün GERÇEK ağırlık merkezi (SurfaceProperties CentreOfMass). Eğrisel/dairesel yüzlerde de doğru — mesh yüz eşleştirme için kritik. */
  yuzMerkez(face: any): { x: number; y: number; z: number } {
    const oc = this.oc;
    // 1) Yüzey ağırlık merkezi (silindir/küre/daire kapakta vertex-ortalaması yanlış olur → CentreOfMass kullan)
    try { const g = new oc.GProp_GProps_1(); oc.BRepGProp.SurfaceProperties_1(face, g, false, false); const c = g.CentreOfMass(); return { x: c.X(), y: c.Y(), z: c.Z() }; } catch (e) { }
    // 2) Fallback: vertex ortalaması
    let sx = 0, sy = 0, sz = 0, n = 0; const seen = new Set<string>();
    const exp = new oc.TopExp_Explorer_2(face, oc.TopAbs_ShapeEnum.TopAbs_VERTEX, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More()) {
      const p = oc.BRep_Tool.Pnt(oc.TopoDS.Vertex_1(exp.Current()));
      const k = p.X().toFixed(3) + ',' + p.Y().toFixed(3) + ',' + p.Z().toFixed(3);
      if (!seen.has(k)) { seen.add(k); sx += p.X(); sy += p.Y(); sz += p.Z(); n++; }
      exp.Next();
    }
    return n ? { x: sx / n, y: sy / n, z: sz / n } : { x: 0, y: 0, z: 0 };
  }

  /** Bir yüzün normali (ilk 3 farklı vertex'ten cross). Sign çağıran tarafça dışa yöneltilir. */
  yuzNormal(face: any): { x: number; y: number; z: number } {
    const oc = this.oc; const pts: any[] = []; const seen = new Set<string>();
    const exp = new oc.TopExp_Explorer_2(face, oc.TopAbs_ShapeEnum.TopAbs_VERTEX, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More() && pts.length < 3) { const p = oc.BRep_Tool.Pnt(oc.TopoDS.Vertex_1(exp.Current())); const k = p.X().toFixed(2) + ',' + p.Y().toFixed(2) + ',' + p.Z().toFixed(2); if (!seen.has(k)) { seen.add(k); pts.push(p); } exp.Next(); }
    if (pts.length < 3) return { x: 0, y: 0, z: 1 };
    const ux = pts[1].X() - pts[0].X(), uy = pts[1].Y() - pts[0].Y(), uz = pts[1].Z() - pts[0].Z();
    const vx = pts[2].X() - pts[0].X(), vy = pts[2].Y() - pts[0].Y(), vz = pts[2].Z() - pts[0].Z();
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const l = Math.hypot(nx, ny, nz) || 1;
    return { x: nx / l, y: ny / l, z: nz / l };
  }

  /** CATIA Thickness (yüz-seçimli): seçili yüzleri normalleri boyunca t kadar öteler (t>0 ekle, t<0 çıkar). */
  yuzeKalinlikEkle(shape: any, faces: any[], t: number): any {
    const oc = this.oc;
    if (!faces?.length || Math.abs(t) < 0.001) return shape;
    let cx = 0, cy = 0, cz = 0; try { const p = new oc.GProp_GProps_1(); oc.BRepGProp.VolumeProperties_1(shape, p, false, false, false); const c = p.CentreOfMass(); cx = c.X(); cy = c.Y(); cz = c.Z(); } catch (e) { }
    let r = shape;
    for (const f of faces) {
      try {
        const n = this.yuzNormal(f), m = this.yuzMerkez(f);
        if ((m.x - cx) * n.x + (m.y - cy) * n.y + (m.z - cz) * n.z < 0) { n.x = -n.x; n.y = -n.y; n.z = -n.z; }   // dışa yönelt
        const slab = new oc.BRepPrimAPI_MakePrism_1(f, new oc.gp_Vec_4(n.x * t, n.y * t, n.z * t), false, true).Shape();
        r = t > 0 ? this.birlestir(r, slab) : this.cikar(r, slab);
      } catch (e) { console.warn('[OCCT yuzeKalinlik]', e); }
    }
    // CATIA gibi TEK sürekli katı: birleşim yerindeki iç dikiş kenarını/aynı-domain yüzleri birleştir
    try { const u = new oc.ShapeUpgrade_UnifySameDomain_2(r, true, true, false); u.Build(); const ru = u.Shape(); if (ru) r = ru; } catch (e) { }
    if (!this.gecerliMi(r)) return null;   // prizma fillet komşusunda yırttıysa → çağıran tüm-gövde offset'e düşsün
    return r;
  }

  /** REPLACE FACE (pratik): seçili planar yüzü hedef düzleme taşır (yüzeyi düzlemle değiştir). Gövde büyür/küçülür. */
  yuzDuzlemeTasi(shape: any, face: any, pO: { x: number; y: number; z: number }, pN: { x: number; y: number; z: number }): any {
    const C = this.yuzMerkez(face); let n = this.yuzNormal(face);
    // gövde merkezi → normali dışa yönelt (yuzeKalinlikEkle ile tutarlı)
    let cx = 0, cy = 0, cz = 0; try { const p = new this.oc.GProp_GProps_1(); this.oc.BRepGProp.VolumeProperties_1(shape, p, false, false, false); const c = p.CentreOfMass(); cx = c.X(); cy = c.Y(); cz = c.Z(); } catch (e) { }
    if ((C.x - cx) * n.x + (C.y - cy) * n.y + (C.z - cz) * n.z < 0) { n = { x: -n.x, y: -n.y, z: -n.z }; }
    const denom = n.x * pN.x + n.y * pN.y + n.z * pN.z;
    if (Math.abs(denom) < 1e-4) return null;   // yüz hedef düzleme paralel değil → taşınamaz
    const t = ((pO.x - C.x) * pN.x + (pO.y - C.y) * pN.y + (pO.z - C.z) * pN.z) / denom;   // dışa-normal boyunca düzleme mesafe
    if (Math.abs(t) < 1e-4) return shape;   // zaten düzlemde
    return this.yuzeKalinlikEkle(shape, [face], t);
  }

  /** Seçili dünya merkez noktalarını OCCT yüzlerine eşler. HER seçili nokta için EN YAKIN yüz merkezi alınır
   * (mesh üçgen-ortalaması ile OCCT yüzey ağırlık merkezi tam çakışmaz; tek tol eşiği eğrisel/öteleme sonrası
   * yüzlerde kaçırır → seçili-yüz op'u sessizce tüm-gövdeye düşerdi). En yakın yüz daima eşleşir; uç durumda
   * (yüz tamamen kayboldu) tol yine de bir tavan görevi görür. */
  /** PERSISTENT NAMING: stableId listesinden şu anki TopoDS_Face'leri verir. Biri çözülemezse [] (çağıran
   *  centroid eşleştirmeye düşer). Radius/op eklense de kimlik taşındığı için doğru yüze bağlanır. */
  seciliYuzlerKimlik(shape: any, stableIds: number[]): any[] {
    if (!this._kimlik || !stableIds?.length) return [];
    const out: any[] = [];
    for (const sid of stableIds) {
      if (sid < 0) return [];
      const fid = this.kimlikFaceId(sid); if (fid < 0) return [];
      const f = this.yuzAl(shape, fid); if (!f) return [];
      out.push(f);
    }
    return out;
  }

  seciliYuzler(shape: any, secNoktalar: { x: number; y: number; z: number }[], tol = 4): any[] {
    if (!secNoktalar?.length) return [];
    const yuzler = this.yuzler(shape);
    if (!yuzler.length) return [];
    const merkezler = yuzler.map(f => this.yuzMerkez(f));
    // Adaptif tavan: model boyutuna göre (öteleme/sadeleştirme yüz merkezini birkaç mm kaydırabilir)
    let cap = Math.max(tol, 4);
    try {
      const oc = this.oc; const bb = new oc.Bnd_Box_1(); oc.BRepBndLib.Add(shape, bb, false);
      const mn = bb.CornerMin(), mx = bb.CornerMax();
      const diag = Math.hypot(mx.X() - mn.X(), mx.Y() - mn.Y(), mx.Z() - mn.Z());
      cap = Math.max(cap, diag * 0.06);   // köşegenin ~%6'sı: yakın yüzü güvenle yakala, komşuyu kaçır
    } catch { }
    const cap2 = cap * cap;
    const secimSet = new Set<number>();
    for (const p of secNoktalar) {
      let enYakin = -1, enKucuk = Infinity;
      for (let i = 0; i < merkezler.length; i++) {
        const m = merkezler[i]; const dx = m.x - p.x, dy = m.y - p.y, dz = m.z - p.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < enKucuk) { enKucuk = d2; enYakin = i; }
      }
      if (enYakin >= 0 && enKucuk <= cap2) secimSet.add(enYakin);
    }
    return [...secimSet].map(i => yuzler[i]);
  }

  /**
   * KUSURSUZ shell (içi boş kabuk): açık yüzler (kaldırılacak) listesi + et kalınlığı.
   * acikFaces boşsa shape'in SON yüzü açılır (üst). BRepOffsetAPI_MakeThickSolid gerçek B-Rep offset.
   */
  shell(shape: any, t: number, acikFaces?: any[]): any {
    const oc = this.oc;
    const acik = (acikFaces && acikFaces.length) ? acikFaces : [this.yuzler(shape).pop()];
    // Arc (fillet/teğet güvenli) → Intersection (keskin) — IsDone + geçerlilik kontrollü (geçersiz/null gövde KAYBETME)
    const dene = (join: any): any => {
      try {
        const list = new oc.TopTools_ListOfShape_1();
        for (const f of acik) if (f) list.Append_1(f);
        const mts = new oc.BRepOffsetAPI_MakeThickSolid_1();
        mts.MakeThickSolidByJoin(shape, list, -Math.abs(t), 1e-3, oc.BRepOffset_Mode.BRepOffset_Skin, false, false, join, false);
        if (mts.IsDone && !mts.IsDone()) return null;
        const s = mts.Shape(); if (!s || !this.gecerliMi(s)) return null;
        return s;
      } catch { return null; }
    };
    return dene(oc.GeomAbs_JoinType.GeomAbs_Arc) || dene(oc.GeomAbs_JoinType.GeomAbs_Intersection) || null;
  }

  /** CATIA Thickness: katıya t kalınlık EKLE/çıkar. Intersection (keskin) → geçersizse Arc (fillet-güvenli) → MakeOffsetShape.
   *  Filletli gövdede Intersection join fillet yüzeyini patlatıyordu; Arc/Offset bunu kusursuz işler. */
  kalinlikEkle(shape: any, t: number): any {
    const oc = this.oc;
    const yonelimDuzelt = (s: any) => {
      try { const props = new oc.GProp_GProps_1(); oc.BRepGProp.VolumeProperties_1(s, props, false, false, false); if (props.Mass() < 0) s = s.Reversed(); }
      catch { s = s.Reversed(); }
      return s;
    };
    const dene = (join: any): any => {
      try {
        const list = new oc.TopTools_ListOfShape_1();   // boş → tüm yüzler offset
        const mts = new oc.BRepOffsetAPI_MakeThickSolid_1();
        mts.MakeThickSolidByJoin(shape, list, t, 1e-3, oc.BRepOffset_Mode.BRepOffset_Skin, false, false, join, true);
        if (mts.IsDone && !mts.IsDone()) return null;
        let s = mts.Shape(); if (!s) return null;
        s = yonelimDuzelt(s);
        if (!this.gecerliMi(s)) return null;   // YIRTIK → reddet, sonraki yönteme geç
        return s;
      } catch { return null; }
    };
    // 1) keskin köşe; 2) fillet/teğet güvenli; 3) en sağlam yüzey offset
    let s = dene(oc.GeomAbs_JoinType.GeomAbs_Intersection);
    if (!s) s = dene(oc.GeomAbs_JoinType.GeomAbs_Arc);
    if (!s) { const o = this.offsetSolid(shape, t); if (o && o !== shape && this.gecerliMi(o)) s = o; }
    return s || shape;
  }

  /** Bir shape'in hacmi (mutlak). Binding yoksa 0. */
  hacim(shape: any): number {
    try { const p = new this.oc.GProp_GProps_1(); this.oc.BRepGProp.VolumeProperties_1(shape, p, false, false, false); return Math.abs(p.Mass()); } catch (e) { return 0; }
  }

  /** CLOSE SURFACE: kapalı yüzey(ler)i dik → shell → katı (BRepBuilderAPI_MakeSolid). */
  katiYap(shapes: any[]): any {
    const oc = this.oc;
    if (!shapes?.length) return null;
    const kapali = shapes.length === 1 ? shapes[0] : this.dikis(shapes);
    try {
      const mk = new oc.BRepBuilderAPI_MakeSolid_1();
      let n = 0;
      const exp = new oc.TopExp_Explorer_2(kapali, oc.TopAbs_ShapeEnum.TopAbs_SHELL, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (exp.More()) { mk.Add(oc.TopoDS.Shell_1(exp.Current())); n++; exp.Next(); }
      if (!n) return null;
      let s = mk.Solid();
      try { const p = new oc.GProp_GProps_1(); oc.BRepGProp.VolumeProperties_1(s, p, false, false, false); if (p.Mass() < 0) s = s.Reversed(); } catch (e) { }
      return s;
    } catch (e) { console.warn('[OCCT katiYap]', e); return null; }
  }

  /** REMOVE LUMP: çok parçalı (kopuk solid) gövdede EN BÜYÜK hacimli solid'i tut. Tek parça ise dokunmaz. */
  enBuyukLump(shape: any): any {
    const oc = this.oc;
    try {
      const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_SOLID, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      let best: any = null, bestV = -1, n = 0;
      while (exp.More()) { const s = oc.TopoDS.Solid_1(exp.Current()); n++; const v = this.hacim(s); if (v > bestV) { bestV = v; best = s; } exp.Next(); }
      return (n > 1 && best) ? best : shape;
    } catch (e) { return shape; }
  }

  /** Bir yüzün kenarları (TopoDS_Edge dizisi). */
  private faceKenarlari(face: any): any[] {
    const oc = this.oc; const out: any[] = [];
    const exp = new oc.TopExp_Explorer_2(face, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
    while (exp.More()) { out.push(oc.TopoDS.Edge_1(exp.Current())); exp.Next(); }
    return out;
  }
  /** İki+ yüzün paylaştığı kenarlar (IsSame). Tek yüzse onun tüm kenarları. */
  ortakKenarlar(faces: any[]): any[] {
    if (!faces?.length) return [];
    if (faces.length === 1) return this.faceKenarlari(faces[0]);
    const out: any[] = []; const eklendi = new Set<any>();
    for (let i = 0; i < faces.length; i++) for (let j = i + 1; j < faces.length; j++) {
      const ea = this.faceKenarlari(faces[i]), eb = this.faceKenarlari(faces[j]);
      for (const x of ea) for (const y of eb) { try { if (x.IsSame(y) && !eklendi.has(x)) { eklendi.add(x); out.push(x); } } catch (e) { } }
    }
    return out;
  }
  /** FACE-FACE / TRITANGENT FILLET: seçili yüzlerin paylaştığı kenarlara r fillet (komşu yüzler). */
  yuzFillet(shape: any, faces: any[], r: number): any {
    const ortak = this.ortakKenarlar(faces);
    if (!ortak.length) return null;   // komşu değil → fillet yok
    try { return this.fillet(shape, r, ortak); } catch (e) { console.warn('[OCCT yuzFillet]', e); return null; }
  }

  /** Bir kenarın uzunluğu (uç vertex mesafesi — yaklaşık, kanıtlı binding). */
  private edgeUzunluk(e: any): number {
    const oc = this.oc;
    try { const v1 = new oc.TopoDS_Vertex(), v2 = new oc.TopoDS_Vertex(); oc.TopExp.Vertices_1(e, v1, v2, false); const p1 = oc.BRep_Tool.Pnt(v1), p2 = oc.BRep_Tool.Pnt(v2); return Math.hypot(p1.X() - p2.X(), p1.Y() - p2.Y(), p1.Z() - p2.Z()); } catch (er) { return 0; }
  }

  /** TRITANGENT FILLET: seçili (orta/şerit) yüzü oto-yarıçapla tangent yuvarla — strip genişliği = en kısa kenar.
   *  Şeridin uzun kenarlarına R=genişlik fillet → yüz iki komşuya teğet biçimde tüketilir. */
  triFillet(shape: any, face: any, rOverride?: number): any {
    const edges = this.faceKenarlari(face);
    if (edges.length < 2) return null;
    const uz = edges.map(e => ({ e, l: this.edgeUzunluk(e) })).filter(o => o.l > 1e-6);
    if (!uz.length) return null;
    const minL = Math.min(...uz.map(o => o.l));
    const r = (rOverride && rOverride > 0) ? rOverride : minL * 0.98;   // oto: şerit genişliği (tam tüketmemek için 0.98)
    const uzun = uz.filter(o => o.l > minL * 1.4).map(o => o.e);        // şeridin uzun kenarları
    const hedef = uzun.length ? uzun : uz.map(o => o.e);
    try { return this.fillet(shape, r, hedef); } catch (e) { console.warn('[OCCT triFillet]', e); return null; }
  }

  /** Bir kenarı içeren ilk yüzü bulur (yüzleri tarayıp IsSame ile eşleştirir — map binding'e gerek yok). */
  private edgeKomsuYuz(shape: any, edge: any): any {
    const oc = this.oc;
    for (const f of this.yuzler(shape)) {
      const exp = new oc.TopExp_Explorer_2(f, oc.TopAbs_ShapeEnum.TopAbs_EDGE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (exp.More()) { try { if (edge.IsSame(exp.Current())) return f; } catch (e) { } exp.Next(); }
    }
    return null;
  }

  /** ASİMETRİK chamfer: Length/Length (d1≠d2) veya Length/Angle (aciMod). Kenarın komşu yüzünü tarayarak bulur. */
  chamferAsim(shape: any, d1: number, d2: number, edges: any[] | undefined, aciMod: boolean): any {
    const oc = this.oc;
    const mc = new oc.BRepFilletAPI_MakeChamfer(shape);
    const liste = (edges && edges.length) ? edges : this.kenarlar(shape);
    let asimEklendi = 0;
    for (const e of liste) {
      const f = this.edgeKomsuYuz(shape, e);
      try {
        if (f && aciMod) { mc.AddDA(d1, d2 * Math.PI / 180, e, f); asimEklendi++; }   // mesafe + açı
        else if (f) { mc.Add_3(d1, d2, e, f); asimEklendi++; }                         // d1 / d2 mesafe
        else mc.Add_2(d1, e);                                                          // yüz yok → simetrik
      } catch (er) { try { mc.Add_2(d1, e); } catch (e2) { } }
    }
    if (!asimEklendi) return null;   // hiç kenar eklenemedi → çağıran sabit chamfer'a düşer
    try { const s = mc.Shape(); return (mc.IsDone && !mc.IsDone()) ? null : s; } catch (e) { console.warn('[OCCT chamferAsim] başarısız', e); return null; }
  }

  /** REMOVE FACE: seçili yüz(ler)i kaldır. 1) Defeaturing ile kapat (fillet/boss gibi healable feature) →
   *  olmazsa 2) yüzü sil, kalan yüzleri açık kabuk olarak dik (CATIA "Remove Face" — her zaman çalışır). */
  yuzKaldir(shape: any, faces: any[]): any {
    const oc = this.oc;
    if (!faces?.length) return null;
    const girisYuz = this.yuzler(shape).length;
    // 1) Defeaturing (iyileştir/kapat) — yalnız healable feature'larda yüz GERÇEKTEN azalır (no-op'u reddet)
    try {
      const df = new oc.BRepAlgoAPI_Defeaturing();
      df.SetShape(shape);
      const list = new oc.TopTools_ListOfShape_1();
      for (const f of faces) list.Append_1(f);
      df.AddFacesToRemove(list);
      try { df.Build(new oc.Message_ProgressRange_1()); } catch (e) { df.Build(); }   // OCCT sürüm farkı: Build(progressRange)
      if (df.IsDone && df.IsDone()) { const s = df.Shape(); if (s && this.gecerliMi(s) && this.yuzler(s).length < girisYuz) return s; }
    } catch (e) { console.warn('[OCCT yuzKaldir defeature]', e); }
    // 2) Fallback: yüzü SİL → kalan yüzleri açık kabuk olarak dik (defeature edilemeyen düz/ana yüzler için).
    //    Eşleştirme MERKEZE göre (IsSame OCCT.js'te güvenilmez) — seciliYuzler ile aynı yöntem.
    try {
      const silMerk = faces.map(rf => { try { return this.yuzMerkez(rf); } catch { return null; } }).filter(Boolean) as { x: number; y: number; z: number }[];
      const kalan: any[] = [];
      const exp = new oc.TopExp_Explorer_2(shape, oc.TopAbs_ShapeEnum.TopAbs_FACE, oc.TopAbs_ShapeEnum.TopAbs_SHAPE);
      while (exp.More()) {
        const f = oc.TopoDS.Face_1(exp.Current());
        const mc = this.yuzMerkez(f);
        let sil = false;
        for (const sm of silMerk) { const dx = mc.x - sm.x, dy = mc.y - sm.y, dz = mc.z - sm.z; if (dx * dx + dy * dy + dz * dz < 1e-4) { sil = true; break; } }
        if (!sil) kalan.push(f);
        exp.Next();
      }
      return (kalan.length && kalan.length < girisYuz) ? (this.dikis(kalan, 1e-4) || null) : null;   // açık kabuk (katı değil)
    } catch (e) { console.warn('[OCCT yuzKaldir kabuk]', e); return null; }
  }
}

/** Tekil paylaşılan kernel (bir kez yüklenir, tüm modelleme paylaşır). */
export const fikoOCCT = new FikoOCCTKernel();
